/**
 * The guide's hero image, which lives at `seo.ogImage` inside `contentJson`.
 *
 * It is read and written through here rather than by hand because `contentJson` is edited as raw
 * JSON in one of the two editors, so a control that writes it has to put the value back without
 * disturbing anything else the author has typed, including formatting. Parse, set one key,
 * re-serialise at the same indent.
 *
 * **`ogImage` does three jobs, which is why one upload is worth this much care.** The public site
 * reads it for the social preview, for the card image on the guide hub (where it currently falls
 * back to a topic tile, because no guide has ever had one) and for the banner at the top of the
 * guide. The one exception is the generated `/api/og` asset: that has the title baked into the
 * picture, so the site deliberately refuses to use it on-page, and so does this.
 */

export interface HeroImage {
  url: string;
  alt: string;
}

/** True for the generated OG asset, which is social-only and must not become a banner. */
export const isGeneratedOgImage = (url: string): boolean => url.includes('/api/og');

export function readHeroImage(contentJson: string): HeroImage | null {
  try {
    const parsed = JSON.parse(contentJson || '{}');
    const url = parsed?.seo?.ogImage;
    if (typeof url !== 'string' || !url.trim() || isGeneratedOgImage(url)) return null;
    return { url, alt: typeof parsed?.seo?.ogImageAlt === 'string' ? parsed.seo.ogImageAlt : '' };
  } catch {
    // A body mid-edit and not parsing is a normal state in this editor, not an error worth
    // surfacing here: the JSON field has its own validation message for that.
    return null;
  }
}

/**
 * Set or clear the hero image, returning the new JSON text.
 *
 * Returns the input unchanged when it does not parse, so a control cannot silently destroy an
 * author's in-progress edit by rewriting it from a partial parse.
 */
export function withHeroImage(contentJson: string, image: HeroImage | null): string {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(contentJson || '{}');
  } catch {
    return contentJson;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return contentJson;

  const seo = { ...((parsed.seo as Record<string, unknown>) ?? {}) };
  if (image) {
    seo.ogImage = image.url;
    seo.ogImageAlt = image.alt;
  } else {
    delete seo.ogImage;
    delete seo.ogImageAlt;
  }

  return JSON.stringify({ ...parsed, seo }, null, 2);
}
